// SPDX-License-Identifier: AGPL-3.0-or-later
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { UnlockedKeys } from "@almanac/crypto";
import type { MessageKey } from "@almanac/i18n";
import { api, ApiError, type Me } from "./lib/api.js";
import { AppContext, type AppContextValue } from "./lib/app.js";
import { MemoryBackend, RemoteBackend } from "./lib/backend.js";
import { demoSnapshot } from "./lib/demo.js";
import { todayIso } from "./lib/figures.js";
import { I18nProvider, useI18n } from "./lib/i18n.js";
import { applyTheme, setPrefs, usePrefs } from "./lib/prefs.js";
import { HouseholdSetup } from "./components/HouseholdSetup.js";
import { UpdateBanner } from "./components/UpdateBanner.js";
import { Store, useStore } from "./lib/store.js";
import { Budgets } from "./pages/Budgets.js";
import { Dashboard } from "./pages/Dashboard.js";
import { Events } from "./pages/Events.js";
import { Import } from "./pages/Import.js";
import { Settings } from "./pages/Settings.js";
import { Transactions } from "./pages/Transactions.js";
import { Year } from "./pages/Year.js";
import { Notifications } from "./pages/Notifications.js";
import { Purchases } from "./pages/Purchases.js";
import { Groups } from "./pages/Groups.js";
import { PushRelay } from "./components/PushRelay.js";
import { allNotices, visibleNotices } from "./lib/notifications.js";
import { SignIn } from "./pages/SignIn.js";
import { Setup } from "./pages/Setup.js";
import { Unlock } from "./pages/Unlock.js";

type Phase =
  | { name: "loading" }
  | { name: "signed-out"; mode: "oidc" | "dev" | "offline"; notice: string | null }
  | { name: "setup" }
  | { name: "locked"; notice: string | null }
  | { name: "ready"; store: Store };

const ROUTES = [
  { path: "dashboard", title: "nav.dashboard", Page: Dashboard },
  { path: "transactions", title: "nav.transactions", Page: Transactions },
  { path: "year", title: "nav.year", Page: Year },
  { path: "budgets", title: "nav.budgets", Page: Budgets },
  { path: "purchases", title: "nav.purchases", Page: Purchases },
  { path: "groups", title: "nav.groups", Page: Groups },
  { path: "events", title: "nav.events", Page: Events },
  { path: "notifications", title: "nav.notifications", Page: Notifications },
  { path: "import", title: "nav.import", Page: Import },
  { path: "settings", title: "nav.settings", Page: Settings },
] as const satisfies ReadonlyArray<{ path: string; title: MessageKey; Page: () => React.JSX.Element }>;

type Route = (typeof ROUTES)[number]["path"];

function routeFromHash(): Route {
  // A page may carry options after "?", e.g. #/transactions?review.
  const path = location.hash.replace(/^#\/?/, "").split("?")[0];
  return (ROUTES.find((r) => r.path === path)?.path ?? "dashboard") as Route;
}

/** Locks after this long without input, dropping the keys from memory. */
const IDLE_LOCK_MS = 15 * 60_000;

export function App() {
  const prefs = usePrefs();
  useEffect(() => {
    applyTheme(prefs);
    const media = matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyTheme(prefs);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [prefs]);
  return (
    <I18nProvider locale={prefs.locale}>
      <Root />
    </I18nProvider>
  );
}

function Root() {
  const { t } = useI18n();
  const [phase, setPhase] = useState<Phase>({ name: "loading" });
  const [me, setMe] = useState<Me | null>(null);

  const start = useCallback(async () => {
    const params = new URLSearchParams(location.search);
    const signin = params.get("signin");
    if (signin) history.replaceState(null, "", location.pathname + location.hash);
    const notice = signin === "failed" ? t("auth.failed") : signin === "expired" ? t("auth.expired") : null;
    let mode: "oidc" | "dev" | "offline" = "offline";
    try {
      mode = (await api.authConfig()).mode;
      const who = await api.me();
      setMe(who);
      setPrefs({ locale: who.locale });
      setPhase(who.hasKeys ? { name: "locked", notice: null } : { name: "setup" });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) setPhase({ name: "signed-out", mode, notice });
      else setPhase({ name: "signed-out", mode: "offline", notice: notice ?? t("auth.offline") });
    }
  }, [t]);

  useEffect(() => {
    void start();
    // Only on first load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openWith = async (keys: UnlockedKeys) => {
    const backend = new RemoteBackend(keys);
    try {
      setPhase({ name: "ready", store: new Store(backend, await backend.load()) });
    } catch (error) {
      setPhase({ name: "locked", notice: t("common.error", { message: (error as Error).message }) });
    }
  };

  const openDemo = async () => {
    const backend = new MemoryBackend(() => demoSnapshot(todayIso()));
    setPhase({ name: "ready", store: new Store(backend, await backend.load()) });
  };

  const signOut = async () => {
    await api.logout().catch(() => undefined);
    location.assign("/");
  };

  switch (phase.name) {
    case "loading":
      return (
        <p className="auth" role="status">
          {t("common.loading")}
        </p>
      );
    case "signed-out":
      return <SignIn mode={phase.mode} notice={phase.notice} onDemo={openDemo} onSignedIn={start} />;
    case "setup":
      return <Setup onUnlocked={openWith} />;
    case "locked":
      return <Unlock notice={phase.notice} onUnlocked={openWith} onSignOut={signOut} />;
    case "ready":
      return (
        <Shell
          store={phase.store}
          me={me}
          onLock={async () => {
            await phase.store.flush().catch(() => undefined);
            if (phase.store.backend.demo) location.reload();
            else setPhase({ name: "locked", notice: null });
          }}
          onSignOut={signOut}
        />
      );
  }
}

function Shell({ store, me, onLock, onSignOut }: { store: Store; me: Me | null; onLock: () => void; onSignOut: () => void }) {
  const { t, money } = useI18n();
  // Amounts in the calendar feed are written in the reader's language.
  store.money = money;
  const state = useStore(store);
  const [route, setRoute] = useState<Route>(routeFromHash);
  const [message, setMessage] = useState("");
  const heading = useRef<HTMLHeadingElement>(null);
  const firstRender = useRef(true);
  const current = ROUTES.find((r) => r.path === route)!;
  const today = todayIso();
  const notices = useMemo(() => allNotices(state, today), [state, today]);
  const unread = visibleNotices(notices, state.notify).length;

  useEffect(() => {
    const onHash = () => setRoute(routeFromHash());
    addEventListener("hashchange", onHash);
    return () => removeEventListener("hashchange", onHash);
  }, []);

  // On navigation, move focus to the new page's heading so screen readers announce it.
  useEffect(() => {
    document.title = `${t(current.title)} · ${t("app.name")}`;
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    heading.current?.focus();
  }, [route, current.title, t]);

  // Lock after a period without activity.
  useEffect(() => {
    if (store.backend.demo) return;
    let timer = setTimeout(onLock, IDLE_LOCK_MS);
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(onLock, IDLE_LOCK_MS);
    };
    const events = ["pointerdown", "keydown", "wheel"] as const;
    events.forEach((e) => addEventListener(e, reset, { passive: true }));
    return () => {
      clearTimeout(timer);
      events.forEach((e) => removeEventListener(e, reset));
    };
  }, [store, onLock]);

  const context = useMemo<AppContextValue>(
    () => ({
      store,
      today,
      announce: (text) => {
        // Clearing first makes repeated messages speak again.
        setMessage("");
        setTimeout(() => setMessage(text), 50);
      },
      lock: onLock,
      signOut: onSignOut,
    }),
    [store, today, onLock, onSignOut],
  );

  return (
    <AppContext.Provider value={context}>
      <a className="skip-link" href="#main">
        {t("nav.skip")}
      </a>
      <div className="shell">
        <header className="sidebar">
          <div className="brand">
            <img src="/icon.svg" alt="" />
            {t("app.name")}
          </div>
          <nav className="nav" aria-label={t("nav.main")}>
            <ul>
              {ROUTES.map((r) => (
                <li key={r.path}>
                  <a href={`#/${r.path}`} aria-current={r.path === route ? "page" : undefined}>
                    {t(r.title)}
                    {r.path === "notifications" && unread > 0 && (
                      <>
                        <span className="badge" aria-hidden="true">
                          {unread}
                        </span>
                        <span className="visually-hidden">{t("notify.unread", { count: unread })}</span>
                      </>
                    )}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
          <div className="sidebar-foot">
            {me?.displayName && <span>{t("auth.signedInAs", { name: me.displayName })}</span>}
            <span aria-live="polite">{state.saving > 0 ? t("common.saving") : ""}</span>
            <button type="button" onClick={onLock}>
              {t("auth.lock")}
            </button>
          </div>
        </header>
        <main id="main" tabIndex={-1}>
          {store.backend.demo && <p className="banner">{t("common.demoBanner")}</p>}
          {state.error && (
            <div className="error-banner" role="alert">
              <span>{t("common.error", { message: state.error })}</span>
              <button type="button" onClick={() => store.dismissError()}>
                {t("common.close")}
              </button>
            </div>
          )}
          {state.profile === null ? (
            <>
              <h1 ref={heading} tabIndex={-1}>
                {t("survey.title")}
              </h1>
              <section className="card" style={{ maxWidth: 720 }}>
                <HouseholdSetup firstRun />
              </section>
            </>
          ) : (
            <>
              <h1 ref={heading} tabIndex={-1}>
                {t(current.title)}
              </h1>
              <current.Page />
            </>
          )}
        </main>
      </div>
      <div className="visually-hidden" aria-live="polite" role="status">
        {message}
      </div>
      <UpdateBanner onBeforeReload={() => store.flush()} />
      <PushRelay notices={notices} />
    </AppContext.Provider>
  );
}
